import { arrondiCentime } from './arrondi.js';
import { plusValueLatente, plusValuePct } from './progression.js';
import { triAnnualise } from './tri.js';
import type { FluxDate, Kpis, PointSerie } from './types.js';

/** Inputs to derive the four indicators of a scenario. */
export interface ParamsKpi {
  /** Monthly series of the selected scenario. */
  readonly serie: readonly PointSerie[];
  /** Current month of the active simulation (RG-05). */
  readonly moisCourant: number;
  /** Validated contributions; arbitrages excluded (RG-06). Include V0 at month 0. */
  readonly versementsValides: readonly FluxDate[];
}

/** Estimated capital = selected scenario series at the current month (RG-05). */
export function capitalAuMois(serie: readonly PointSerie[], moisCourant: number): number {
  const dernier = serie.length - 1;
  const point = serie[Math.min(Math.max(moisCourant, 0), dernier)];
  return point?.capital ?? 0;
}

/** Total contributed = sum of validated flows, arbitrages counted as zero (RG-06). */
export function totalVerse(versementsValides: readonly FluxDate[]): number {
  return arrondiCentime(versementsValides.reduce((s, f) => s + f.montant, 0));
}

/** Compute the four indicators (RG-05..RG-08) for one scenario. */
export function calculerKpis(params: ParamsKpi): Kpis {
  const capitalEstime = capitalAuMois(params.serie, params.moisCourant);
  const verse = totalVerse(params.versementsValides);
  const pv = plusValueLatente(capitalEstime, verse);
  const pct = plusValuePct(pv, verse);
  const rendement = triAnnualise(params.versementsValides, capitalEstime, params.moisCourant);
  return {
    capitalEstime,
    totalVerse: verse,
    rendementAnnualise: rendement,
    plusValueLatente: pv,
    plusValuePct: pct,
  };
}
